import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  calcAgeFromDMY,
  checkIvfEligibility,
  createOrUpdateMedicalAttendance,
  fetchApplicationTypes,
  fetchDiseases,
  fetchHospitals,
  fetchPatientNames,
  formDateToApiDate,
  getEmployeeDisplayName,
  getFileExtension,
  getLoggedInUser,
  normalizeDobToDMY,
  padEmpId,
  uploadMedicalAttendanceFile,
  ymdToDmyDots,
} from "./medicalAttendance.service";

type UploadedDoc = {
  fileName: string;
  fileSize: string;
  fileExtension: string;
  serverKey: string;
  type: string;
};

const CreateMedicalAttendance: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const user = getLoggedInUser();
  const details = user?.details || {};
  const empId = padEmpId();
  const employeeName = getEmployeeDisplayName(user);
  const isMaleUser = String(details?.gender ?? "").trim() === "1";

  const [applicationOptions, setApplicationOptions] = useState<{ id: string; label: string }[]>([]);
  const [hospitalOptions, setHospitalOptions] = useState<{ id: string; label: string }[]>([]);
  const [diseaseOptions, setDiseaseOptions] = useState<{ id: string; label: string }[]>([]);
  const [patientNameOptions, setPatientNameOptions] = useState<any[]>([]);
  const [applicationOptionsLoading, setApplicationOptionsLoading] = useState(false);
  const [hospitalOptionsLoading, setHospitalOptionsLoading] = useState(false);
  const [diseaseOptionsLoading, setDiseaseOptionsLoading] = useState(false);
  const [patientNameOptionsLoading, setPatientNameOptionsLoading] = useState(false);
  const [ivfEligibilityLoading, setIvfEligibilityLoading] = useState(false);
  const [ivfEligible, setIvfEligible] = useState<boolean | null>(null);
  const [ivfSelfPatient, setIvfSelfPatient] = useState<{ label: string; patientDob: string } | null>(null);
  const [spouseFromApi, setSpouseFromApi] = useState<{ name: string; dob: string } | null>(null);
  const [prescriptionDocs, setPrescriptionDocs] = useState<UploadedDoc[]>([]);
  const [otherDocs, setOtherDocs] = useState<UploadedDoc[]>([]);
  const [docLoading, setDocLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [eligibilityMessage, setEligibilityMessage] = useState("");

  const [formData, setFormData] = useState({
    activePhoneNumber: String(details?.phone || details?.mobile || user?.phone || "").replace(/\D/g, "").slice(0, 10),
    emailId: String(details?.email || user?.email || ""),
    treatmentType: "",
    patientName: "",
    relationToEmployee: "",
    age: "",
    hospitalName: "",
    hospitalAddress: "",
    disease: "",
    treatmentFrom: "",
    treatmentTill: "",
    ivfFor: isMaleUser ? "SPOUSE" : "SELF",
    maritalStatus: "",
    fromHospital: "",
    spouseName: "",
    spouseDob: "",
    declaration1: false,
    declaration2: false,
    declaration3: false,
    declaration4: false,
    govHospitalName: "",
    govHospitalAddress: "",
  });

  const variant =
    formData.treatmentType === "66"
      ? "ivf"
      : formData.treatmentType === "62"
        ? "para62"
        : formData.treatmentType === "63"
          ? "para63"
          : null;

  const selectedHospitalLabel =
    hospitalOptions.find((opt) => opt.id === formData.hospitalName)?.label || "";
  const isGovernmentHospital =
    variant === "para63" && selectedHospitalLabel.toLowerCase().includes("government hospital");
  const selectedFromHospitalLabel =
    hospitalOptions.find((opt) => opt.id === formData.fromHospital)?.label || "";
  const isGovernmentHospitalIVF =
    variant === "ivf" && selectedFromHospitalLabel.toLowerCase().includes("government hospital");

  const formatEmployeeDob = () => {
    const dob = details?.birthdate || details?.dob || details?.DOB;
    return dob ? normalizeDobToDMY(dob) : "";
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setApplicationOptionsLoading(true);
      try {
        const options = await fetchApplicationTypes();
        if (!cancelled) setApplicationOptions(options);
      } catch (err: any) {
        if (!cancelled) toast.error(Api.getErrorMessage(err) || "Failed to load application types");
      } finally {
        if (!cancelled) setApplicationOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const requestType = formData.treatmentType;
    if (!["62", "63", "66"].includes(requestType)) {
      setHospitalOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setHospitalOptionsLoading(true);
      try {
        const options = await fetchHospitals(requestType);
        if (!cancelled) setHospitalOptions(options);
      } catch {
        if (!cancelled) {
          toast.error("Failed to load hospitals");
          setHospitalOptions([]);
        }
      } finally {
        if (!cancelled) setHospitalOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.treatmentType]);

  useEffect(() => {
    const requestType = formData.treatmentType;
    if (requestType !== "62" && requestType !== "63") {
      setDiseaseOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setDiseaseOptionsLoading(true);
      try {
        const options = await fetchDiseases(requestType);
        if (!cancelled) setDiseaseOptions(options);
      } catch {
        if (!cancelled) {
          toast.error("Failed to load diseases");
          setDiseaseOptions([]);
        }
      } finally {
        if (!cancelled) setDiseaseOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.treatmentType]);

  const extractSpouseFromEligibility = (body: any): { name: string; dob: string } | null => {
    const inner = body?.data ?? {};
    const src: any = { ...(body || {}), ...(inner || {}) };
    const name = String(
      src.SpouseName ?? src.spouseName ?? src.SpouseNameEng ?? src.spouse_name ?? ""
    ).trim();
    const dob = normalizeDobToDMY(
      src.SpouseDOB ?? src.SpouseDob ?? src.spouseDob ?? src.SpouseDateOfBirth ?? src.spouseDateOfBirth ?? src.spouse_dob ?? ""
    );
    if (!name && !dob) return null;
    return { name, dob };
  };

  const runIvfEligibility = useCallback(async () => {
    if (!empId) return;
    setIvfEligibilityLoading(true);
    setIvfEligible(null);
    setSpouseFromApi(null);
    setEligibilityMessage("");
    setFormData((prev) => ({ ...prev, spouseName: "", spouseDob: "" }));
    try {
      const res: any = await checkIvfEligibility(empId);
      const body = res?.data ?? res;
      const inner = body?.data ?? body;
      const isIvf = inner?.isIvf;
      const topMessage = String(body?.message || res?.message || "").trim();
      const detailMessage = String(inner?.message || "").trim();
      if (res?.success !== false && isIvf === true) {
        setIvfEligible(true);
        const spouse = extractSpouseFromEligibility(body);
        if (spouse) {
          setSpouseFromApi(spouse);
          setFormData((prev) => ({ ...prev, spouseName: spouse.name, spouseDob: spouse.dob }));
        }
      } else {
        setIvfEligible(false);
        setSpouseFromApi(null);
        const msg = detailMessage || topMessage || "Not eligible for IVF";
        setEligibilityMessage(msg);
        toast.error(topMessage || "Not eligible for IVF");
      }
    } catch (err: any) {
      setIvfEligible(false);
      setSpouseFromApi(null);
      toast.error(Api.getErrorMessage(err) || "Unable to verify IVF eligibility");
    } finally {
      setIvfEligibilityLoading(false);
    }
  }, [empId]);

  useEffect(() => {
    if (variant !== "ivf") {
      setIvfSelfPatient(null);
      return;
    }
    let cancelled = false;
    const empFullName = employeeName === "-" ? "Self" : employeeName;
    const empDob = formatEmployeeDob();
    if (!empId) return;
    fetchPatientNames(empId)
      .then((arr) => {
        if (cancelled) return;
        const selfRec = arr.find(
          (d: any) =>
            String(d.RelationToEmployee ?? d.relationToEmployee ?? "").trim().toUpperCase() === "SELF" ||
            String(d.Value ?? "").toUpperCase() === "SELF" ||
            String(d.Value ?? "") === "0"
        );
        const selfDob = normalizeDobToDMY(
          selfRec?.PatientDOB ?? selfRec?.patientDob ?? selfRec?.DOB ?? selfRec?.dob ?? ""
        );
        setIvfSelfPatient({
          label: String(selfRec?.PatientName ?? selfRec?.patientName ?? "").trim() || empFullName || "Self",
          patientDob: selfDob || empDob,
        });
        const spouseRec = arr.find(
          (d: any) =>
            String(d.RelationToEmployee ?? d.relationToEmployee ?? "").trim().toLowerCase() === "spouse"
        );
        if (spouseRec) {
          const spouse = {
            name: String(spouseRec.PatientName ?? spouseRec.patientName ?? "").trim(),
            dob: normalizeDobToDMY(
              spouseRec.PatientDOB ?? spouseRec.patientDob ?? spouseRec.DOB ?? spouseRec.dob ?? ""
            ),
          };
          setSpouseFromApi(spouse);
          setFormData((prev) =>
            prev.ivfFor === "SPOUSE" ? { ...prev, spouseName: spouse.name, spouseDob: spouse.dob } : prev
          );
        }
      })
      .catch(() => {
        if (!cancelled) setIvfSelfPatient({ label: empFullName || "Self", patientDob: empDob });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, empId]);

  const loadPatientNames = async () => {
    if (!empId) {
      toast.error(t("employee_id_not_found"));
      return;
    }
    setPatientNameOptionsLoading(true);
    try {
      const arr = await fetchPatientNames(empId);
      const empFullName = employeeName === "-" ? "Self" : employeeName;
      setPatientNameOptions(
        arr.map((d: any) => {
          const value = String(d.Value ?? d.value ?? "").trim();
          const patientName = String(d.PatientName ?? d.patientName ?? "").trim();
          const relation = String(d.RelationToEmployee ?? d.relationToEmployee ?? "").trim();
          const age = String(d.PatientAge ?? d.patientAge ?? "").trim();
          const isSelfOpt = relation.toUpperCase() === "SELF" || value === "0";
          return {
            id: value,
            label: patientName || (isSelfOpt ? empFullName || "Self" : relation),
            relationToEmployee: isSelfOpt ? "SELF" : relation,
            relationLabel: relation,
            patientAge: age,
            isSelf: isSelfOpt,
          };
        })
      );
    } catch (err: any) {
      setPatientNameOptions([]);
      toast.error(Api.getErrorMessage(err) || "Failed to load relationships");
    } finally {
      setPatientNameOptionsLoading(false);
    }
  };

  const handleInputChange = (key: string, value: any) => {
    if (key === "age" && value) {
      const ageNum = parseInt(value, 10);
      if (!isNaN(ageNum) && ageNum > 0 && ageNum < 130) {
        const today = new Date();
        const dobDate = new Date(today.getFullYear() - ageNum, today.getMonth(), today.getDate());
        const dd = String(dobDate.getDate()).padStart(2, "0");
        const mm = String(dobDate.getMonth() + 1).padStart(2, "0");
        setFormData((prev) => ({
          ...prev,
          [key]: value,
          patientDob: `${dd}.${mm}.${dobDate.getFullYear()}`,
        }));
        return;
      }
    }
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const onTreatmentTypeChange = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      treatmentType: id,
      hospitalName: "",
      hospitalAddress: "",
      disease: "",
      patientName: "",
      relationToEmployee: "",
      age: "",
      treatmentFrom: "",
      treatmentTill: "",
      maritalStatus: id === "66" ? "Married" : "",
      fromHospital: "",
      govHospitalName: "",
      govHospitalAddress: "",
      spouseName: "",
      spouseDob: "",
      ivfFor: id === "66" ? (isMaleUser ? "SPOUSE" : "SELF") : prev.ivfFor,
      declaration1: false,
      declaration2: false,
      declaration3: false,
      declaration4: false,
    }));
    setPrescriptionDocs([]);
    setOtherDocs([]);
    setEligibilityMessage("");
    if (id === "66") {
      runIvfEligibility();
    } else {
      setIvfEligible(null);
      setSpouseFromApi(null);
    }
    if (id === "62" || id === "63") {
      loadPatientNames();
    }
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];
    if (!formData.treatmentType) errors.push("Application for");
    if (!formData.activePhoneNumber.trim()) errors.push("Active Phone Number");
    if (!formData.emailId.trim()) errors.push("Email");

    if (variant === "ivf") {
      if (!formData.maritalStatus.trim()) errors.push("Marital status");
      if (!formData.fromHospital) errors.push("From Hospital");
      if (isGovernmentHospitalIVF) {
        if (!formData.govHospitalName.trim()) errors.push("Hospital Name (Government)");
        if (!formData.govHospitalAddress.trim()) errors.push("Hospital Address (Government)");
      }
      if (formData.ivfFor === "SPOUSE" && spouseFromApi) {
        if (!formData.spouseName.trim()) errors.push("Spouse name");
        if (!formData.spouseDob) errors.push("Spouse date of birth");
      }
      if (!formData.declaration1 || !formData.declaration2 || !formData.declaration3 || !formData.declaration4) {
        errors.push("All declarations (checkboxes)");
      }
    }

    if (variant === "para62") {
      if (!formData.patientName) errors.push("Patient Name");
      if (!formData.relationToEmployee.trim()) errors.push("Relation to employee");
      if (!formData.age.trim()) errors.push("Patient Age");
      if (!formData.disease) errors.push("Disease");
      if (!formData.hospitalName.trim()) errors.push("Hospital Name");
      if (!formData.hospitalAddress.trim()) errors.push("Hospital Address");
    }

    if (variant === "para63") {
      if (!formData.patientName) errors.push("Patient Name");
      if (!formData.relationToEmployee.trim()) errors.push("Relation to employee");
      if (!formData.age.trim()) errors.push("Patient Age");
      if (!formData.disease) errors.push("Disease");
      if (!formData.hospitalName) errors.push("Hospital");
      if (isGovernmentHospital) {
        if (!formData.govHospitalName.trim()) errors.push("Hospital Name (Government)");
        if (!formData.govHospitalAddress.trim()) errors.push("Hospital Address (Government)");
      }
      if (!formData.treatmentFrom) errors.push("Treatment date from");
      if (!formData.treatmentTill) errors.push("Treatment date till");
    }

    if (variant && prescriptionDocs.length === 0) {
      errors.push("Upload Doctor's Prescription (at least one file)");
    }
    return errors;
  };

  const buildSubmitPayload = () => {
    const requestType = formData.treatmentType;
    const patientLabel =
      patientNameOptions.find((opt) => opt.id === formData.patientName)?.label || employeeName;

    if (requestType === "66") {
      const isSpouse = formData.ivfFor === "SPOUSE";
      const patientName = isSpouse
        ? (formData.spouseName || "SPOUSE").slice(0, 30)
        : ivfSelfPatient?.label || employeeName || "Self";
      const patientDobDMY = isSpouse
        ? formData.spouseDob
        : ivfSelfPatient?.patientDob || formatEmployeeDob();
      const fromHospitalLabel =
        hospitalOptions.find((opt) => opt.id === formData.fromHospital)?.label || "";
      const isGovHospIVF = fromHospitalLabel.toLowerCase().includes("government hospital");
      const ivfPayload: any = {
        Action: "C",
        ReferenceNumber: "",
        EmpID: empId,
        EmailID: formData.emailId.trim(),
        ContactNumber: formData.activePhoneNumber.trim(),
        PatientName: patientName,
        PatientDOB: formDateToApiDate(patientDobDMY),
        Age: calcAgeFromDMY(patientDobDMY),
        RelationToEmployee: isSpouse ? "SPOUSE" : "SELF",
        RequestTypeForMedicalAttendanceRules: requestType,
        HospitalRecordKey: formData.fromHospital || "",
        MaritalStatus: formData.maritalStatus ? formData.maritalStatus.charAt(0).toUpperCase() : "M",
        ToDocument: {
          results: [
            ...prescriptionDocs.map((doc) => ({
              FileName: doc.serverKey,
              FileExtension: doc.type || getFileExtension(doc.fileName, doc.type),
              TypeOfDocument: "01",
            })),
            ...otherDocs.map((doc) => ({
              FileName: doc.serverKey,
              FileExtension: doc.type || getFileExtension(doc.fileName, doc.type),
              TypeOfDocument: "05",
            })),
          ],
        },
      };
      if (isGovHospIVF) {
        ivfPayload.HospitalName = formData.govHospitalName.trim();
        ivfPayload.HospitalAddress = formData.govHospitalAddress.trim();
      }
      return ivfPayload;
    }

    if (requestType === "62") {
      return {
        Action: "C",
        ReferenceNumber: "",
        EmpID: empId,
        EmailID: formData.emailId.trim(),
        ContactNumber: formData.activePhoneNumber.trim(),
        RequestTypeForMedicalAttendanceRules: requestType,
        PatientName: patientLabel,
        RelationToEmployee:
          formData.relationToEmployee === "SELF"
            ? "SELF"
            : formData.patientName || formData.relationToEmployee || "SELF",
        HospitalRecordKey: "",
        HospitalName: formData.hospitalName.trim(),
        HospitalAddress: formData.hospitalAddress.trim(),
        Age: formData.age.trim(),
        DiseaseForProlongTreatmentPara62: formData.disease || "",
        ToDocument: {
          results: [
            ...prescriptionDocs.map((doc) => ({
              ReferenceNumber: "",
              EmpID: empId,
              FileName: doc.fileName,
              FileExtension: getFileExtension(doc.fileName, doc.type),
              TypeOfDocument: "01",
              key: doc.serverKey || "",
            })),
            ...otherDocs.map((doc) => ({
              ReferenceNumber: "",
              EmpID: empId,
              FileName: doc.fileName,
              FileExtension: getFileExtension(doc.fileName, doc.type),
              TypeOfDocument: "05",
              key: doc.serverKey || "",
            })),
          ],
        },
      };
    }

    const hospitalLabel =
      hospitalOptions.find((opt) => opt.id === formData.hospitalName)?.label || "";
    const isGovHospPayload = hospitalLabel.toLowerCase().includes("government hospital");
    return {
      Action: "C",
      RequestTypeForMedicalAttendanceRules: requestType,
      PatientName: patientLabel,
      HospitalRecordKey: formData.hospitalName || "",
      HospitalName: isGovHospPayload ? formData.govHospitalName.trim() : hospitalLabel,
      HospitalAddress: isGovHospPayload ? formData.govHospitalAddress.trim() : "",
      EmailID: formData.emailId.trim(),
      ContactNumber: formData.activePhoneNumber.trim(),
      Age: formData.age.trim(),
      RelationToEmployee:
        formData.relationToEmployee === "SELF"
          ? "SELF"
          : formData.patientName || formData.relationToEmployee || "SELF",
      EmpDateOfTreatmentBegin: formDateToApiDate(ymdToDmyDots(formData.treatmentFrom)),
      EmpDateOfTreatmentEnd: formDateToApiDate(ymdToDmyDots(formData.treatmentTill)),
      DiseaseForProlongTreatmentPara63: formData.disease || "",
      ToDocument: {
        results: [
          ...prescriptionDocs.map((doc) => ({
            EmpID: empId,
            RequestTypeForMedicalAttendanceRules: requestType,
            FileName: doc.serverKey,
            FileExtension: getFileExtension(doc.fileName, doc.type),
            FileSize: doc.fileSize || "",
            TypeOfDocument: "01",
          })),
          ...otherDocs.map((doc) => ({
            EmpID: empId,
            RequestTypeForMedicalAttendanceRules: requestType,
            FileName: doc.serverKey,
            FileExtension: getFileExtension(doc.fileName, doc.type),
            FileSize: doc.fileSize || "",
            TypeOfDocument: "05",
          })),
        ],
      },
    };
  };

  const handleFileChoose = async (section: "prescription" | "other", files: FileList | null) => {
    if (!files || files.length === 0) return;
    const singlePrescription = (variant === "para62" || variant === "para63") && section === "prescription";
    if (singlePrescription && prescriptionDocs.length >= 1) {
      toast.error(t("only_one_document_allowed") || "Only one document is allowed");
      return;
    }
    const list = Array.from(files).slice(0, singlePrescription ? 1 : files.length);
    setDocLoading(true);
    try {
      for (const file of list) {
        const uploaded = await uploadMedicalAttendanceFile(file);
        if (section === "prescription") {
          setPrescriptionDocs((prev) => (singlePrescription ? [uploaded] : [...prev, uploaded]));
        } else {
          setOtherDocs((prev) => [...prev, uploaded]);
        }
        toast.success(`"${uploaded.fileName}" uploaded`);
      }
    } catch (err: any) {
      toast.error(err?.message || Api.getErrorMessage(err) || "Upload failed");
    } finally {
      setDocLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const missing = validateForm();
    if (missing.length > 0) {
      toast.error(`Please fill: ${missing.join(", ")}`);
      return;
    }
    if (!empId) {
      toast.error(t("employee_id_not_found"));
      return;
    }
    setSubmitting(true);
    toast.info("Submitting your request, please wait...");
    try {
      const payload = buildSubmitPayload();
      const res: any = await createOrUpdateMedicalAttendance(payload);
      if (res?.success === false) {
        toast.error(res?.message || "Failed to submit");
        return;
      }
      setShowThankYou(true);
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const designation =
    details?.designation_details?.longText ||
    details?.sap_department_designation_wings?.designation_name ||
    "-";
  const department = details?.sap_department_designation_wings?.department_name || "-";
  const canSubmit = variant && !(variant === "ivf" && ivfEligible !== true) && !submitting && !docLoading;

  const pageBusy = applicationOptionsLoading || ivfEligibilityLoading;

  const required = <span className="text-danger">*</span>;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{t("application_prior_approval_ivf_para")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.medicalAttendance.list}>{t("prior_approval_for_ivf_para_6_2_6_3_treatment")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("create_new_request")}
                </li>
              </ol>
            </nav>
          </div>
          <Link to={routes.medicalAttendance.list} className="btn btn-light">
            <i className="ti ti-arrow-left me-1"></i>
            {t("close")}
          </Link>
        </div>

        {pageBusy ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
            {ivfEligibilityLoading && <p className="mt-2 text-muted">Checking IVF eligibility...</p>}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <Section title={t("employee_information") || "Employee Information"}>
              <div className="row g-3">
                <ReadOnlyField label={t("name")} value={employeeName} />
                <ReadOnlyField label={t("designation")} value={designation} />
                <ReadOnlyField label={t("department")} value={department} />
                <ReadOnlyField label={t("payscale") || "Payscale"} value={details?.pay_scale || "-"} />
                <ReadOnlyField
                  label={t("mode_of_recruitment") || "Mode of Recruitment"}
                  value={user?.employmentType || details?.employmentType || "-"}
                />
                <ReadOnlyField
                  label={t("employment_status") || "Employment Status"}
                  value={details?.empStatus || user?.empStatus || "-"}
                />
              </div>
            </Section>

            <Section title="Contact Information">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {t("active_phone_number") || "Active Phone Number"} {required}
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    maxLength={10}
                    value={formData.activePhoneNumber}
                    onChange={(e) => handleInputChange("activePhoneNumber", e.target.value.replace(/\D/g, ""))}
                    placeholder={t("enter_phone_number") || "Enter phone number"}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {t("email_id") || "Email ID"} {required}
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    value={formData.emailId}
                    onChange={(e) => handleInputChange("emailId", e.target.value)}
                    placeholder={t("enter_email") || "Enter email"}
                  />
                </div>
              </div>
            </Section>

            <Section title={t("application_for") || "Application For"}>
              <label className="form-label fw-semibold">
                {t("application_for") || "Application For"} {required}
              </label>
              <select
                className="form-select"
                value={formData.treatmentType}
                onChange={(e) => onTreatmentTypeChange(e.target.value)}
              >
                <option value="">{t("select_application_type")}</option>
                {applicationOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Section>

            {variant === "ivf" && (
              <Section title={t("ivf_treatment_taken_for") || "IVF Treatment Taken For"}>
                {eligibilityMessage && (
                  <div className="alert alert-warning">{eligibilityMessage}</div>
                )}
                <label className="form-label fw-semibold">
                  {t("ivf_treatment_taken_for")} {required}
                </label>
                <div className="d-flex gap-4 mb-3">
                  <div className="form-check" style={{ opacity: isMaleUser ? 0.38 : 1 }}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ivfFor"
                      id="ivfSelf"
                      disabled={isMaleUser}
                      checked={formData.ivfFor === "SELF"}
                      onChange={() =>
                        setFormData((prev) => ({
                          ...prev,
                          ivfFor: "SELF",
                          maritalStatus: "Married",
                          fromHospital: "",
                          spouseName: "",
                          spouseDob: "",
                        }))
                      }
                    />
                    <label className="form-check-label" htmlFor="ivfSelf">
                      {t("self") || "SELF"}
                    </label>
                  </div>
                  <div className="form-check" style={{ opacity: !isMaleUser ? 0.38 : 1 }}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ivfFor"
                      id="ivfSpouse"
                      disabled={!isMaleUser}
                      checked={formData.ivfFor === "SPOUSE"}
                      onChange={() =>
                        setFormData((prev) => ({
                          ...prev,
                          ivfFor: "SPOUSE",
                          fromHospital: "",
                          spouseName: spouseFromApi?.name || "",
                          spouseDob: spouseFromApi?.dob || "",
                        }))
                      }
                    />
                    <label className="form-check-label" htmlFor="ivfSpouse">
                      {t("spouse") || "Spouse"}
                    </label>
                  </div>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      {t("marital_status") || "Marital Status"} {required}
                    </label>
                    <input
                      className="form-control"
                      value={formData.maritalStatus}
                      onChange={(e) => handleInputChange("maritalStatus", e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      {t("from_hospital") || "From Hospital"} {required}
                    </label>
                    <select
                      className="form-select"
                      value={formData.fromHospital}
                      disabled={hospitalOptionsLoading}
                      onChange={(e) => {
                        const id = e.target.value;
                        const newLabel = hospitalOptions.find((opt) => opt.id === id)?.label || "";
                        const newIsGov = newLabel.toLowerCase().includes("government hospital");
                        setFormData((prev) => ({
                          ...prev,
                          fromHospital: id,
                          ...(newIsGov ? {} : { govHospitalName: "", govHospitalAddress: "" }),
                        }));
                      }}
                    >
                      <option value="">{hospitalOptionsLoading ? t("loading_") || "Loading..." : t("select_hospital") || "Select hospital"}</option>
                      {hospitalOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {isGovernmentHospitalIVF && (
                    <>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          {t("hospital_name")} {required}
                        </label>
                        <input
                          className="form-control"
                          value={formData.govHospitalName}
                          onChange={(e) => handleInputChange("govHospitalName", e.target.value)}
                          placeholder={t("enter_hospital_name")}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          {t("hospital_address")} {required}
                        </label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={formData.govHospitalAddress}
                          onChange={(e) => handleInputChange("govHospitalAddress", e.target.value)}
                          placeholder={t("enter_hospital_address")}
                        />
                      </div>
                    </>
                  )}
                  {formData.ivfFor === "SELF" && (
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">{t("dob_employee")}</label>
                      <input
                        className="form-control"
                        value={ivfSelfPatient?.patientDob || formatEmployeeDob()}
                        disabled
                      />
                    </div>
                  )}
                  {formData.ivfFor === "SPOUSE" && spouseFromApi && (
                    <>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          {t("spouse_name")} {required}
                        </label>
                        <input className="form-control" value={formData.spouseName} disabled />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          {t("spouse_dob")} {required}
                        </label>
                        <input className="form-control" value={formData.spouseDob} disabled />
                      </div>
                    </>
                  )}
                </div>
                <div className="alert alert-warning mt-3 mb-0">
                  <strong>{t("mandatory_attachments")}:</strong>
                  <div>{t("prescription_failure_conventional")}</div>
                </div>
              </Section>
            )}

            {(variant === "para62" || variant === "para63") && (
              <Section title={variant === "para62" ? "Para 6.2" : "Para 6.3"}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      {t("patient_name")} {required}
                    </label>
                    <select
                      className="form-select"
                      value={formData.patientName}
                      disabled={patientNameOptionsLoading}
                      onFocus={() => {
                        if (patientNameOptions.length === 0 && !patientNameOptionsLoading) loadPatientNames();
                      }}
                      onChange={(e) => {
                        const id = e.target.value;
                        const opt = patientNameOptions.find((o: any) => o.id === id);
                        if (opt) {
                          const isSelf = !!opt.isSelf || String(opt.id ?? "").toUpperCase() === "0";
                          setFormData((prev) => ({
                            ...prev,
                            patientName: id,
                            relationToEmployee: isSelf
                              ? "SELF"
                              : opt.relationLabel || opt.relationToEmployee || String(opt.id ?? ""),
                            age: opt.patientAge ? String(opt.patientAge) : prev.age,
                          }));
                        } else {
                          handleInputChange("patientName", id);
                        }
                      }}
                    >
                      <option value="">
                        {patientNameOptionsLoading ? "Loading..." : t("select_patient")}
                      </option>
                      {patientNameOptions.map((opt: any) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      {t("relation_to_employee")} {required}
                    </label>
                    <input className="form-control" value={formData.relationToEmployee} disabled />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      {t("patient_age")} {required}
                    </label>
                    <input
                      className="form-control"
                      inputMode="numeric"
                      maxLength={3}
                      value={formData.age}
                      onChange={(e) => handleInputChange("age", e.target.value.replace(/\D/g, ""))}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      {t("disease")} {required}
                    </label>
                    <select
                      className="form-select"
                      value={formData.disease}
                      disabled={diseaseOptionsLoading}
                      onChange={(e) => handleInputChange("disease", e.target.value)}
                    >
                      <option value="">
                        {diseaseOptionsLoading ? "Loading..." : t("select_disease")}
                      </option>
                      {diseaseOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {variant === "para62" ? (
                    <>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          {t("hospital_name")} {required}
                        </label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={formData.hospitalName}
                          onChange={(e) => handleInputChange("hospitalName", e.target.value)}
                          placeholder={t("enter_hospital_name")}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          {t("hospital_address")} {required}
                        </label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={formData.hospitalAddress}
                          onChange={(e) => handleInputChange("hospitalAddress", e.target.value)}
                          placeholder={t("enter_hospital_address")}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          {t("hospital")} {required}
                        </label>
                        <select
                          className="form-select"
                          value={formData.hospitalName}
                          disabled={hospitalOptionsLoading}
                          onChange={(e) => {
                            const id = e.target.value;
                            const newLabel = hospitalOptions.find((opt) => opt.id === id)?.label || "";
                            const newIsGov = newLabel.toLowerCase().includes("government hospital");
                            setFormData((prev) => ({
                              ...prev,
                              hospitalName: id,
                              ...(newIsGov ? {} : { govHospitalName: "", govHospitalAddress: "" }),
                            }));
                          }}
                        >
                          <option value="">
                            {hospitalOptionsLoading ? "Loading..." : t("select_hospital") || "Select hospital"}
                          </option>
                          {hospitalOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {isGovernmentHospital && (
                        <>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">
                              {t("hospital_name")} {required}
                            </label>
                            <input
                              className="form-control"
                              value={formData.govHospitalName}
                              onChange={(e) => handleInputChange("govHospitalName", e.target.value)}
                            />
                          </div>
                          <div className="col-12">
                            <label className="form-label fw-semibold">
                              {t("hospital_address")} {required}
                            </label>
                            <textarea
                              className="form-control"
                              rows={2}
                              value={formData.govHospitalAddress}
                              onChange={(e) => handleInputChange("govHospitalAddress", e.target.value)}
                            />
                          </div>
                        </>
                      )}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          {t("date_of_treatment_to_be_taken_from")} {required}
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          min={new Date().toISOString().slice(0, 10)}
                          value={formData.treatmentFrom}
                          onChange={(e) => handleInputChange("treatmentFrom", e.target.value)}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          {t("date_of_treatment_to_be_taken_till")} {required}
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          min={formData.treatmentFrom || new Date().toISOString().slice(0, 10)}
                          value={formData.treatmentTill}
                          onChange={(e) => handleInputChange("treatmentTill", e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>
              </Section>
            )}

            {variant && (
              <>
                <Section title={t("upload_doctor_prescription")}>
                  <label className="form-label fw-semibold">
                    {t("upload_doctor_prescription")} {required}
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg"
                    multiple={!(variant === "para62" || variant === "para63")}
                    disabled={
                      docLoading ||
                      ((variant === "para62" || variant === "para63") && prescriptionDocs.length >= 1)
                    }
                    onChange={(e) => {
                      handleFileChoose("prescription", e.target.files);
                      e.target.value = "";
                    }}
                  />
                  <small className="text-muted d-block mt-1">
                    PDF (max 2 MB) or JPG/JPEG (max 5 MB)
                    {(variant === "para62" || variant === "para63") ? " — only one prescription file" : ""}
                  </small>
                  <DocList
                    docs={prescriptionDocs}
                    onDelete={(i) => setPrescriptionDocs((prev) => prev.filter((_, idx) => idx !== i))}
                  />
                </Section>

                <Section title={t("any_other_documents")}>
                  <input
                    type="file"
                    className="form-control"
                    accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg"
                    multiple
                    disabled={docLoading}
                    onChange={(e) => {
                      handleFileChoose("other", e.target.files);
                      e.target.value = "";
                    }}
                  />
                  <DocList
                    docs={otherDocs}
                    onDelete={(i) => setOtherDocs((prev) => prev.filter((_, idx) => idx !== i))}
                  />
                </Section>
              </>
            )}

            {variant === "ivf" && (
              <Section title={t("declarations_title")}>
                {[1, 2, 3, 4].map((n) => (
                  <div className="form-check mb-2" key={n}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`decl${n}`}
                      checked={(formData as any)[`declaration${n}`]}
                      onChange={(e) => handleInputChange(`declaration${n}`, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`decl${n}`}>
                      {t(`declaration${n}`)}
                    </label>
                  </div>
                ))}
              </Section>
            )}

            {variant && (
              <div className="d-flex justify-content-end gap-2 mb-4">
                <Link to={routes.medicalAttendance.list} className="btn btn-light">
                  {t("cancel")}
                </Link>
                <button type="submit" className="btn btn-danger" disabled={!canSubmit}>
                  {submitting || docLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      {docLoading ? "Uploading..." : "Submitting..."}
                    </>
                  ) : (
                    t("submit")
                  )}
                </button>
              </div>
            )}
          </form>
        )}

        {showThankYou && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center py-4">
                  <i className="ti ti-circle-check text-success fs-1 d-block mb-2"></i>
                  <h5>{t("thank_you_application_submitted_successfully") || "Thank you. Application submitted successfully."}</h5>
                  <button
                    className="btn btn-danger mt-3"
                    onClick={() => navigate(routes.medicalAttendance.list)}
                  >
                    {t("ok") || "OK"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="card border-0 shadow-sm mb-3">
    <div className="card-header bg-white">
      <h5 className="mb-0 fw-bold">{title}</h5>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const ReadOnlyField: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="col-md-6">
    <label className="form-label fw-semibold">{label}</label>
    <input className="form-control" value={value ?? "-"} disabled />
  </div>
);

const DocList: React.FC<{ docs: UploadedDoc[]; onDelete: (i: number) => void }> = ({ docs, onDelete }) => {
  if (docs.length === 0) return null;
  return (
    <ul className="list-group mt-2">
      {docs.map((doc, i) => (
        <li className="list-group-item d-flex justify-content-between align-items-center" key={`${doc.serverKey}-${i}`}>
          <span>
            {doc.fileName} <small className="text-muted">({doc.fileSize})</small>
          </span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onDelete(i)}>
            <i className="ti ti-trash"></i>
          </button>
        </li>
      ))}
    </ul>
  );
};

export default CreateMedicalAttendance;
